import type { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import type { IRealNameIdentification } from '@/services/identification/interface';
import type { PersonnelManageAPI } from '@/services/userManage/interface';
import type { ValueOf } from 'type-fest';

export enum PrepayConsumeEnum {
  YES = 'Yes',
  NO = 'No',
}

export enum PaymentMethodEnum {
  PAYONEER = 'payoneer',
  AIRWALLEX = 'airwallex',
  WORLDFIRST = 'worldfirst',
  LAKALA = 'lakala',
  HSVA = 'hsva',
  WATER_BILL = 'water_bill',
}

export const BLUEVISION = 'BV';

export type CurrentUserInfo = {
  isPrepayConsume: PrepayConsumeEnum; // 是否预付实销
  fbUserInfo: {
    fbUserList: {
      id: string;
      name: string;
    }[];
    oauthUrl: string;
  };
  grayscale: {
    roiBest: boolean;
    inflowWithdraw: boolean; // 水单充值
    collectOpinions: boolean; // 用户反馈能力
    bindBm: boolean; // 绑定BM邮件确认 灰度
    buFinConf: boolean; // 财务配置 灰度
    teamManage: boolean; // 团队管理 灰度
    emergingMediaAiAssistEnabled?: boolean; // 新兴媒体AI开户灰度
    disabledPool?: boolean; // 封禁池/提交封禁预提 灰度（true 时展示提交封禁预提按钮）
    openaiEnabled?: boolean; // OpenAI开户灰度（开户申请、开户列表、账户管理列表，灰度内才显示）
    ttRiskVlDashboardEnabled?: boolean; // TT风控大盘灰度（true 时 TikTok 媒体消耗质量 tab 才展示）
  };
  groupInfo: PersonnelManageAPI.Response.GroupInfo[];
  from: number;
  isMcc: number;
  role: number;
  username: string;
  menus?: [];
  access?: string;
  walletInfo?: {
    walletId: string;
    balance: string;
    currency: string;
    isPrepay: boolean;
    rate: number;
    canRecharge: boolean; // 能否充值 西红柿签约 隐藏水单充值、收银台及VA打款功能
    // 支付方式
    paymentMethod?: PaymentMethodEnum[];
    // 预留金额
    reservedAmount?: string;
  };
  // 信用卡卡包信息
  creditCard?: {
    // 卡包id
    pkgId: number;
    // 总额度上限
    totalSpendCap: number;
    // 总剩余可用额度
    totalSpendRemains: number;
    // 总卡数
    totalCardNumber: number;
    // 创建时间
    createTime: string;
    // 更新时间
    updateTime: string;
    // 卡列表信息
    cardList: {
      // 卡id
      cardId: number;
      // 卡额度上限
      spendCap: number;
      // 卡剩余额度
      spendRemains: number;
      // 媒体范围
      mediums: [];
      // 卡类型:Reserved：兜底卡，Normal：普通卡
      cardType: 'Reserved' | 'Normal';
      // 卡状态:Normal：正常，Disabled：冻结
      status: 'Normal' | 'Disabled';
      // 创建时间
      createTime: string;
      // 更新时间
      updateTime: string;
    }[];
    // 支付方式
    paymentMethod?: PaymentMethodEnum[];
  };

  /** 企业信息 */
  companyInfo: {
    /** 付款公司名称 */
    companyName: string;
    /** 乙方主体 */
    ourEntity: string;
    /** 签约ID */
    signId: number;
    /** 业务单元 */
    signBusinessUnit: number;
  };
  /** 我的信息 */
  userInfo: {
    /** 用户名称 */
    contactName: string;
    /** 手机号 */
    phone: string;
  };

  teamInfo: {
    /** 团队名称 */
    teamName: string;
    /** 团队ID */
    teamId: string;
    /** 团队角色 */
    teamRole: ValueOf<typeof PersonnelManageAPI.ROLE>;
  };

  // 是否开通恒生VA
  haveHsva: boolean;
  customerConsumptionConfigured: boolean;

  // 是否开通刷单监控
  customerBrushMonitoring: boolean;

  // 是否开通广告指导
  guidanceExist: boolean;

  /** 实名认证状态 */
  verifyInfo: IRealNameIdentification.Response.IdentificationInfoRes;
  claimStatus: IRealNameIdentification.Common.ClaimStatus;

  /** 企业认证相关 start ------------------------------------------------------ */
  /** 是否显示企业认证信息（内部开通显示） */
  signCertificationShow: boolean;
  /** 企业认证信息 */
  signEnterpriseInfo?: {
    /** 认证状态 */
    certificationStatus?: IEnterpriseCertification.Common.EnterpriseCertificationStatus;
    /** 认证明细列表 */
    certificationInfos?: {
      /** 认证ID */
      id: string;
      /** 认证人 */
      certifier: string;
      /** 认证企业 */
      certifiedEnterprise: string;
      /** 认证时间 */
      certifiedTime: string;
    }[];
  };
  /** 企业认证 - 个人认证信息 */
  signIndividualInfo?: {
    /** 认证状态 */
    certificationStatus?: IEnterpriseCertification.Common.EnterpriseCertificationStatus;
    /** 认证明细列表 */
    certificationInfos?: {
      /** 认证ID */
      id: string;
      /** 认证人 */
      certifier: string;
      /** 认证时间 */
      certifiedTime: string;
    }[];
  };
  /** 默认认证类型（客户签约对应的类型） Individual/Enterprise 个人/企业 */
  defaultCertificationType?: IEnterpriseCertification.Common.CertificationType;
  /** 认证状态  Uncertified/Certified/ING 未认证/已认证/认证中 */
  signCertificationStatus?: IEnterpriseCertification.Common.EnterpriseCertificationStatus;
  /** 是否需要认证 */
  signCertificationRequired?: boolean;
  /** 验证进行中URL */
  pendingCertificationUrl?: string;
  /** 企业认证相关 end ------------------------------------------------------ */

  /** 绑定BM确认邮箱 （VL 主子账号所有【注册邮箱】 + 签约联系人） */
  bmBindingConfirmEmails?: string[];

  /** 权限 */
  permissions?: {
    // TT东南亚开户权限
    tiktokSeaUsd: boolean;
  };
} | null;
